import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { sharedGet, invalidateSharedGet } from '@/lib/sharedGet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on every request so httpOnly access/refresh tokens flow back.
axios.defaults.withCredentials = true;

// ── Bearer-token fallback ────────────────────────────────────────────────
// Some hosting layers rewrite the CORS response to `Access-Control-Allow-Origin: *`
// even when the FastAPI middleware sends the specific origin. Browsers then
// reject the httpOnly Set-Cookie silently and the session is lost. To stay
// resilient we ALSO cache the access_token returned in the login body and
// attach it as an Authorization header — the backend accepts either source.
const TOKEN_KEY = 'sb_access_token';
export function setAccessToken(tok) {
  try {
    if (tok) sessionStorage.setItem(TOKEN_KEY, tok);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable */ }
}
export function getAccessToken() {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
}
axios.interceptors.request.use((config) => {
  try {
    const t = getAccessToken();
    if (t) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    }
  } catch { /* noop */ }
  return config;
});

// Attach the active 製造 department (X-Department-Id) to manufacturing API calls.
// 資材 (procurement) is central and is filtered explicitly via query params instead.
axios.interceptors.request.use((config) => {
  try {
    const url = config.url || '';
    if (url.includes('/api/mfg/') || url.includes('/api/scanner/')) {
      const dep = localStorage.getItem('mfg_active_department');
      if (dep && dep !== 'all') {
        config.headers = config.headers || {};
        config.headers['X-Department-Id'] = dep;
      }
    }
  } catch (e) { /* localStorage unavailable */ }
  return config;
});

// FastAPI can raise `detail` as an object (structured conflicts) or as a
// validation array. Both used to be handed straight to toast()/JSX, which
// crashes React with "Objects are not valid as a React child" and blanks the
// screen. Normalise `detail` to a string once, here, and keep the structured
// payload on `detail_obj` for callers that need it (e.g. current_version).
axios.interceptors.response.use(undefined, (error) => {
  try {
    const data = error?.response?.data;
    const d = data?.detail;
    if (d && typeof d === 'object') {
      data.detail_obj = d;
      if (Array.isArray(d)) {
        data.detail = d.map((x) => x?.msg || x?.message).filter(Boolean).join(' / ')
          || '入力内容をご確認ください';
      } else {
        data.detail = [d.message, d.msg, d.error, d.reason]
          .find((v) => typeof v === 'string' && v.trim()) || '処理に失敗しました';
      }
    }
  } catch { /* never let error handling throw */ }
  return Promise.reject(error);
});

const AuthContext = createContext({
  user: null,            // null while loading, false if not authenticated, object if logged in
  login: async () => {},
  logout: async () => {},
  refreshMe: async () => {},
});

// Role helpers — read the server-seeded `user.capabilities` map when present,
// fall back to role-based defaults for older sessions / unauthenticated callers.
function caps(user) {
  return (user && typeof user === 'object' && user.capabilities) || null;
}
export function isAdmin(user) {
  return !!user && typeof user === 'object' && user.role === 'admin';
}
export function isManager(user) {
  return !!user && typeof user === 'object' && user.role === 'manager';
}
// シンプルメニュー（設定 → モジュール設定・既定 ON）。
// ON のとき、同じ仕事の画面をひとつにまとめ、マスタは 設定 → マスター に寄せる。
export function isSimpleMenu(user) {
  if (!user || typeof user !== 'object') return true;
  return user.simple_menu !== false;
}
/** シンプルメニュー中に隠している製造画面を、管理者が個別に戻しているか。 */
export function navFlag(user, key) {
  return !!((user && user.nav_flags) || {})[key];
}

export function canManageDirectory(user) {
  const c = caps(user);
  if (c) return !!(c.employees || c.departments);
  return isAdmin(user) || isManager(user);
}
export function canManageEmail(user) {
  const c = caps(user);
  if (c) return !!c.email;
  return isAdmin(user);
}
export function canManageUsers(user) {
  const c = caps(user);
  if (c) return !!c.users;
  return isAdmin(user);
}
export function canViewAuditLog(user) {
  const c = caps(user);
  if (c) return !!c.audit_log;
  return isAdmin(user);
}
// AI実行ログ (metadata-only AI Operation Logs) — server capability `ai_logs_view`; admin default ON.
export function canViewAiLogs(user) {
  const c = caps(user);
  if (c) return !!c.ai_logs_view;
  return isAdmin(user);
}
export function canSeeAllSubmissions(user) {
  const c = caps(user);
  if (c) return !!c.all_submissions;
  return isAdmin(user) || isManager(user);
}
export function canVerify(user) {
  const c = caps(user);
  if (c) return !!c.verify;
  return isAdmin(user) || isManager(user);
}
export function canApprove(user) {
  const c = caps(user);
  if (c) return !!c.approve;
  return isAdmin(user) || isManager(user);
}
export function canEnterSettings(user) {
  const c = caps(user);
  if (c) return !!c.settings;
  return isAdmin(user) || isManager(user);
}
export function canManageEmployees(user) {
  const c = caps(user);
  if (c) return !!c.employees;
  return isAdmin(user) || isManager(user);
}
export function canManageDepartments(user) {
  const c = caps(user);
  if (c) return !!c.departments;
  return isAdmin(user) || isManager(user);
}
// Every authenticated user may now enter the Settings area (to manage their own
// signature, sign their own submissions, and view departments). Individual pages
// stay capability/admin gated.
export function canSeeSettings(user) {
  return !!user && typeof user === 'object';
}

// Per-app EDIT/MANAGE capabilities. Fall back to the legacy manager-or-admin
// rule only for old sessions with no capabilities map.
// 2026-07: broad keys were split (create/edit/delete/approve/publish/move).
// These helpers mean "has ANY management capability in that app" (for showing
// management UI); use hasCap(user, key) for precise per-action gating.
export function hasCap(user, key) {
  if (isAdmin(user)) return true;
  const c = caps(user);
  return !!(c && c[key]);
}
export function anyCap(user, keys) {
  if (isAdmin(user)) return true;
  const c = caps(user);
  return !!(c && keys.some((k) => c[k]));
}
export function canEditMfg(user) {
  const c = caps(user);
  if (c) return !!(c.mfg_edit || c.mfg_delete);
  return isAdmin(user) || isManager(user);
}
export function canEditShizai(user) {
  const c = caps(user);
  if (c) return !!(c.shizai_edit || c.shizai_create || c.shizai_delete || c.shizai_approve);
  return isAdmin(user) || isManager(user);
}
export function canRequestParts(user) {
  if (isAdmin(user)) return true;
  const c = caps(user);
  return !!(c && c.shizai_request);
}
export function canEditIt(user) {
  const c = caps(user);
  if (c) return !!c.it_edit;
  return isAdmin(user) || isManager(user);
}
export function canEditKnowledge(user) {
  const c = caps(user);
  if (c) return !!(c.knowledge_edit || c.knowledge_create || c.knowledge_delete || c.knowledge_publish);
  return isAdmin(user) || isManager(user);
}
export function canManageChat(user) {
  const c = caps(user);
  if (c) return !!(c.chat_channel_create || c.chat_manage);
  return isAdmin(user) || isManager(user);
}
export function canManagePlanner(user) {
  const c = caps(user);
  if (c) return !!(c.planner_manage || c.planner_create || c.planner_edit || c.planner_move || c.planner_delete);
  return isAdmin(user) || isManager(user);
}
export function canReleaseWo(user) {
  const c = caps(user);
  if (c) return !!c.mfg_wo_release;
  return isAdmin(user) || isManager(user);
}
export function canEditLookups(user) {
  const c = caps(user);
  if (c) return !!c.lookups_edit;
  return !!user && typeof user === 'object';
}
export function canManageLabels(user) {
  const c = caps(user);
  if (c) return !!(c.label_create || c.label_edit || c.label_delete || c.label_manage);
  return isAdmin(user) || isManager(user);
}
export function canBackup(user) {
  const c = caps(user);
  if (c) return !!c.backup;
  return isAdmin(user);
}
export function canAdvancedSettings(user) {
  const c = caps(user);
  if (c) return !!c.settings_advanced;
  return isAdmin(user);
}
export function canManageSku(user) {
  const c = caps(user);
  if (c) return !!c.sku_manage;
  return isAdmin(user) || isManager(user);
}
export function canAiImportHistory(user) {
  const c = caps(user);
  if (c) return !!c.ai_import_history;
  return isAdmin(user) || isManager(user);
}

// Which top-level launcher apps a role can open. Defaults to true when the cap
// is missing (backfilled server-side) so nobody is accidentally locked out.
// Globally paused modules (設定 → モジュール設定) override everything — admins too.
export function canUseApp(user, appId) {
  const paused = (user && typeof user === 'object' && user.paused_modules) || [];
  if (paused.includes(appId)) return false;
  if (isAdmin(user)) return true;
  const c = caps(user);
  if (!c) return true;
  const v = c[`app_${appId}`];
  return v === undefined ? true : !!v;
}

// Admin-only settings routes (mirrors <RequireAdmin> in App.js). Everything else
// under /settings is capability-gated (see canBackup, canAdvancedSettings, etc.)
const ADMIN_ONLY_SETTINGS = [
  '/settings/locations',
  '/settings/taxonomy',
  '/settings/users',
  '/settings/roles',
  '/settings/modules',
  '/settings/email-templates',
  '/settings/email-log',
  '/settings/email',
  '/settings/audit-log',
  '/settings/data-audit',
  '/settings/storage',
];
// Backup + workflow/master/holidays/notifications-flavour pages are gated by
// their own capabilities, not by admin role. Managers with backup or
// settings_advanced granted may access them.
const BACKUP_SETTINGS = ['/settings/backup'];
const ADVANCED_SETTINGS = [
  '/settings/workflow',
  '/settings/lookups',
  '/settings/order-categories',
  '/settings/holidays',
  '/settings/shizai-notifications',
  '/settings/admin-notify',
  '/settings/shizai-approvals',
];
export function canAccessSettingsPath(user, pathname) {
  if (!pathname || !pathname.startsWith('/settings')) return false;
  if (pathname.startsWith('/settings/signature')) return canVerify(user);
  if (BACKUP_SETTINGS.some((p) => pathname.startsWith(p))) return canBackup(user);
  if (ADVANCED_SETTINGS.some((p) => pathname.startsWith(p))) return canAdvancedSettings(user);
  if (ADMIN_ONLY_SETTINGS.some((p) => pathname.startsWith(p))) return isAdmin(user);
  return canEnterSettings(user);
}

export function AuthProvider({ children }) {
  // null = unknown / still checking; false = not logged in; object = logged in
  const [user, setUser] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      // StrictMode は effect を2回走らせるため、同じ /auth/me が二重に出ていた。
      const me = await sharedGet(`${API}/auth/me`, { ttl: 2000 });
      setUser(me);
      // Once authenticated, race-probe the LAN in the background so photo
      // downloads from the NAS can go directly over the local network.
      // Skipped on preview/dev hosts — the probe targets 192.168.x NAS URLs
      // which just spam CORS errors in the console outside the office LAN.
      // (Settings → ストレージ の手動プローブは常に実行可能)
      const host = window.location.hostname;
      const isPreviewHost = host === 'localhost' || host.endsWith('.preview.emergentagent.com');
      if (!isPreviewHost) {
        import('../lib/lanStorage').then(({ probeLan, getLanBase }) => {
          if (getLanBase()) return; // already probed this session
          axios.get(`${API}/storage/lan-probes`)
            .then((r) => probeLan(r.data?.urls || []))
            .catch(() => { /* backend down / not configured — silent */ });
        });
      }
      return me;
    } catch (_e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Keep the DateTimeService timezone in sync with the logged-in user. The
  // display LOCALE (date separators) is owned by LanguageContext (header
  // toggle), so we only set the timezone here.
  useEffect(() => {
    import('@/utils/datetime').then(({ setUserTimezone }) => {
      setUserTimezone((user && typeof user === 'object' && user.timezone) || 'Asia/Tokyo');
    });
  }, [user]);

  // Auto-redirect to /login on any 401 from a protected endpoint
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || '';
        if (status === 401 && !url.endsWith('/auth/login') && !url.endsWith('/auth/me')) {
          setUser(false);
          // Public token pages (共有ガント /g/…, 承認リンク) must never bounce to login
          const path = typeof window !== 'undefined' ? window.location.pathname : '';
          const isPublicPage = path.startsWith('/g/') || path.startsWith('/shizai/approvals/token');
          if (typeof window !== 'undefined' && !isPublicPage && !path.startsWith('/login')) {
            const next = window.location.pathname + window.location.search;
            window.location.replace(`/login?next=${encodeURIComponent(next)}`);
          }
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Cache the access_token from the response body in case the browser
    // dropped the httpOnly cookie (proxy CORS rewrite). See top-of-file note.
    if (res.data?.access_token) setAccessToken(res.data.access_token);
    invalidateSharedGet('/auth/me');
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_e) { /* ignore */ }
    setAccessToken(null);
    invalidateSharedGet('/auth/me');
    // Wipe the cached LAN base so the next user starts fresh (they may be on
    // a different network — e.g. shared kiosk PC).
    import('../lib/lanStorage').then(({ clearLanBase }) => clearLanBase());
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Turns an API `detail` into text safe to show a user.
// It must NEVER JSON.stringify an object into a toast: server payloads carry
// machine-readable `codes`, and one of them printed a container path and Debian
// package names to an engineer trying to send a report. Structured payloads
// expose only their `detail`/`message` sentence; anything unrecognised falls
// back to a generic line and the raw shape goes to the console for support.
export function formatApiErrorDetail(detail) {
  if (detail == null) return 'エラーが発生しました。';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : null)).filter(Boolean).join(' ')
      || 'エラーが発生しました。';
  }
  if (typeof detail === 'object') {
    // `{detail, codes, debug}` — the shape the PDF renderer returns
    if (typeof detail.detail === 'string') return detail.detail;
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.msg === 'string') return detail.msg;
    console.warn('[api] unrecognised error detail shape', detail);
  }
  return 'エラーが発生しました。';
}
